import clsx from 'clsx';
import { ComponentPropsWithoutRef, forwardRef, useId, useState } from 'react';
import { ReactComponent as InputClearIcon } from 'src/shared/components/icons/input-clear-icon.svg';
import { twMerge } from 'tailwind-merge';

const show = false;
interface InputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'onChange' | 'size'> {
  label?: string;
  noLabel?: boolean;
  size: 'sm' | 'lg';
  error?: string | null;
  onChange?: (value: string) => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ id, size = 'lg', label, noLabel = false, error, className, onChange, ...rest }, ref) => {
    const [value, setValue] = useState('');
    const newId = useId();
    const finalId = id || newId;

    return (
      <div className={twMerge(clsx('flex flex-col font-medium font-aeonik w-full', className))}>
        {label && (
          <label
            htmlFor={finalId}
            className={clsx('w-full text-white', {
              'mb-1 text-[1.25rem]/[1.625rem]': size === 'sm',
              'mb-2 text-[1.5rem]/[1.71rem]': size === 'lg',
            })}
          >
            {label}
          </label>
        )}

        <div
          className={clsx('w-full relative flex items-center', {
            'h-12': size === 'sm',
            'h-22.5': size === 'lg',
          })}
        >
          <input
            ref={ref}
            id={finalId}
            aria-label={label}
            value={value}
            aria-describedby={`clearBtn-${finalId}`}
            onChange={(e) => {
              setValue(e.target.value);
              onChange?.(e.target.value);
            }}
            {...rest}
            className={clsx(
              'w-full h-full border-2 outline-none focus:outline-none',

              {
                'bg-green-dark border-red-error text-red-error focus:bg-green-forest': error,
                'bg-green-dark border-green-laser text-green-laser focus:bg-green-forest': !error,
                'rounded-[1.25rem] text-[2.5rem]/[2.875rem] ps-5': size === 'lg',
                'rounded-[0.5rem] text-[1.25rem]/[1.625rem] ps-3.5': size === 'sm',
              }
            )}
          />

          {value && show && (
            <button
              type="button"
              id={`clearBtn-${finalId}`}
              aria-label="Clear input"
              aria-controls={finalId}
              className={clsx(
                'absolute flex items-center justify-center bg-transparent outline-none focus:outline-none focus:bg-green-forest',
                {
                  'w-16 h-16 right-3.5 rounded-[0.875rem]': size === 'lg',
                  'w-8 h-8 right-2 rounded-2': size === 'sm',
                }
              )}
              onClick={() => {
                setValue('');
                onChange?.('');
                document.getElementById(finalId)?.focus();
              }}
            >
              <InputClearIcon
                className={clsx({
                  'text-red-error': error,
                  'text-green-laser': !error,
                  'w-7.5 h-7.5': size === 'lg',
                  'w-3.5 h-3.5': size === 'sm',
                })}
              />
            </button>
          )}
        </div>
        {!noLabel && (
          <label
            className={clsx('w-full h-fit sm:h-7 align-bottom text-red-error', {
              'mt-1 text-[1.25rem]/[1.625rem]': size === 'sm',
              'mt-2 text-[1.5rem]/[1.71rem]': size === 'lg',
            })}
          >
            {error}
          </label>
        )}
      </div>
    );
  }
);
